import { Text } from "@hackthenorth/north";
import moment from "moment";
import { Button, Modal } from "north.js";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { Header, PageWrapper } from "src/shared/components";
import { Route } from "src/shared/constants/route";
import { useUserContext } from "src/shared/contexts";

import { decodeBadgeContacts } from "./contacts";
import { useGetBadgeContactsQuery } from "./graphql/getBadgeContacts.generated";

export default function BadgeConnect() {
  const { id } = useUserContext();
  const { data, loading, error, refetch } = useGetBadgeContactsQuery({
    variables: { userId: id as number },
    skip: !id,
    fetchPolicy: "no-cache",
  });
  const backup =
    data?.claims[0]?.user_id === id
      ? data.claims[0].fields_HACKTHENORTH2026_APPLICATIONS.answers
          .badge_data_json
      : null;
  const backupValue = backup?.value;
  const decoded = useMemo(
    () => decodeBadgeContacts(backupValue),
    [backupValue]
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  const selected = decoded.contacts.find(
    (contact) => contact.badgeId === selectedId
  );

  return (
    <>
      <Header
        title="Hacker Badge: Connect"
        subtitle="The people you’ve met through your badge"
      />
      <PageWrapper>
        <Content
          data-private="true"
          data-hj-suppress="true"
          className="ph-no-capture"
        >
          <Text>
            Visit <Link to={Route.BADGE_SYNC}>Sync</Link> or a sync station to
            fetch the latest list from your badge. People you bump with will
            appear here after you sync.
          </Text>
          {backup && Date.parse(backup.updated_at) > 0 && (
            <Text mods="grey">
              Last synced: {moment(backup.updated_at).format("lll")} ·{" "}
              {moment(backup.updated_at).from(now)}
            </Text>
          )}
          {loading ? (
            <Text role="status">Loading your connections…</Text>
          ) : error || decoded.error ? (
            <>
              <Text role="alert">
                {decoded.error ||
                  "We couldn’t load your connections. Please try again."}
              </Text>
              <Button
                color="secondary"
                onClick={() => void refetch().catch(() => undefined)}
              >
                Retry
              </Button>
            </>
          ) : (
            <>
              {decoded.partial && (
                <Text role="status">
                  This backup is incomplete, so some contacts may be missing.
                  Sync again to update the list.
                </Text>
              )}
              <ContactHeading>
                <Text mods="h3 heading">
                  {decoded.contacts.length}{" "}
                  {decoded.contacts.length === 1 ? "connection" : "connections"}
                </Text>
                <Button
                  color="secondary"
                  size="sm"
                  disabled={decoded.contacts.length === 0}
                  onClick={() => {
                    const fields = [
                      "Email",
                      "Phone",
                      "LinkedIn",
                      "Discord",
                      "Instagram",
                      "X / Twitter",
                    ];
                    const rows = [
                      [
                        "Name",
                        "Role",
                        ...fields.flatMap((field) =>
                          field === "Discord"
                            ? [field]
                            : [field, `${field} link`]
                        ),
                        "Badge timestamp (seconds; unsynchronized)",
                        "Last synced",
                      ],
                      ...decoded.contacts.map((contact) => [
                        contact.name,
                        contact.role,
                        ...fields.flatMap((field) => {
                          const detail = contact.details.find(
                            ({ label }) => label === field
                          );
                          return field === "Discord"
                            ? [detail?.value || ""]
                            : [detail?.value || "", detail?.href || ""];
                        }),
                        contact.receivedUnix == null
                          ? ""
                          : String(contact.receivedUnix),
                        backup?.updated_at || "",
                      ]),
                    ];
                    const url = URL.createObjectURL(
                      new Blob(
                        [
                          "\uFEFF",
                          rows
                            .map((row) =>
                              row
                                .map((value) => {
                                  // Quoting alone does not stop spreadsheet formulas in
                                  // attendee-supplied names/handles (or +phone numbers).
                                  const text =
                                    /^[\s\uFEFF]*[=+\-@]|^[\t\r\n]/.test(value)
                                      ? `'${value}`
                                      : value;
                                  return `"${text.replace(/"/g, '""')}"`;
                                })
                                .join(",")
                            )
                            .join("\r\n"),
                          "\r\n",
                        ],
                        { type: "text/csv;charset=utf-8;" }
                      )
                    );
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `hack-the-north-contacts-${moment().format(
                      "YYYY-MM-DD"
                    )}.csv`;
                    document.body.appendChild(link);
                    try {
                      link.click();
                    } finally {
                      link.remove();
                      // Let the browser start the download before releasing it.
                      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
                    }
                  }}
                >
                  Download CSV
                </Button>
              </ContactHeading>
              {decoded.contacts.length === 0 ? (
                <Text>
                  No connections saved yet. Bump badges with someone, then sync
                  your badge to see their shared contact information here.
                </Text>
              ) : (
                <Cards>
                  {decoded.contacts.map((contact) => (
                    <ContactCard
                      key={contact.badgeId}
                      type="button"
                      onClick={() => setSelectedId(contact.badgeId)}
                      aria-label={`View ${contact.name}’s contact information`}
                    >
                      <Text as="span" mods="bold">
                        {contact.name}
                      </Text>
                      <Text as="span" mods="grey">
                        {contact.role}
                      </Text>
                      <Text as="span">View contact info →</Text>
                    </ContactCard>
                  ))}
                </Cards>
              )}
            </>
          )}
        </Content>
        <Modal open={!!selected} onClose={() => setSelectedId(null)}>
          <Modal.Header>
            <span
              data-private="true"
              data-hj-suppress="true"
              className="ph-no-capture"
            >
              {selected?.name}
            </span>
          </Modal.Header>
          <Modal.Body>
            <Content
              data-private="true"
              data-hj-suppress="true"
              className="ph-no-capture"
            >
              <Text>{selected?.role}</Text>
              {selected?.details.length === 0 && (
                <Text>
                  This person hasn’t shared contact links on their badge.
                </Text>
              )}
              {selected?.details.map((detail) => (
                <ContactDetail key={detail.label}>
                  <Text mods="bold">{detail.label}</Text>
                  {detail.href ? (
                    <a
                      href={detail.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      referrerPolicy="no-referrer"
                    >
                      {detail.href.startsWith("https:")
                        ? detail.href
                        : detail.value}
                    </a>
                  ) : (
                    <Text>{detail.value}</Text>
                  )}
                </ContactDetail>
              ))}
            </Content>
          </Modal.Body>
          <Modal.Footer justify="end">
            <Button color="secondary" onClick={() => setSelectedId(null)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </PageWrapper>
    </>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 800px;
  overflow-wrap: anywhere;
  p {
    margin: 0;
  }
`;
const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  gap: 16px;
`;
const ContactHeading = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;
const ContactCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 24px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  text-align: left;
  font: inherit;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  }
`;
const ContactDetail = styled.div`
  a {
    text-decoration: underline;
  }
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
